"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.escapeLikePattern = exports.applyFilter = void 0;
const filter_types_1 = require("./filter-types");
/**
 * Построить параметризованный SQL `WHERE`-fragment из AST-фильтра.
 *
 * Гарантии безопасности:
 * 1. **Prepared statements only** — values уходят через `$N` placeholders, не
 *    string-конкатенация. SQL injection в value невозможен.
 * 2. **Field whitelist double-check** — `allowedFields.includes(field)` runtime;
 *    throw если field неизвестен (zod должен был отбить, fail-safe здесь).
 * 3. **LIKE pattern escaping** (default) — special chars `%`/`_`/`\` экранируются.
 *    Wildcard-pattern требует явный `likeMode: 'raw'` от admin'а (только для
 *    `like`/`ilike`). `contains`/`starts_with`/`ends_with` экранируют ВСЕГДА
 *    (не читают `likeMode`) и оборачивают значение в `%` сами — безопасны для
 *    любой роли, wildcard от клиента структурно невозможен.
 * 4. **`tenantId` никогда не в filter** — отбивается через `allowedFields` (caller
 *    обязан исключить). Postgres RLS — backup на DB-уровне.
 *
 * @example
 *   const { sql, params } = applyFilter(ast, {
 *     allowedFields: ['name', 'status', 'createdAt'],
 *     fieldMapping: { createdAt: 'created_at' },
 *     paramOffset: 1,  // если в WHERE уже есть $1 = tenant_id
 *   });
 *   await client.query(`SELECT * FROM foos WHERE tenant_id = $1 AND ${sql}`, [tenantId, ...params]);
 */
const applyFilter = (ast, opts) => {
    const params = [];
    const paramOffset = opts.paramOffset ?? 0;
    const likeMode = opts.likeMode ?? 'safe';
    const addParam = (value) => {
        params.push(value);
        return `$${paramOffset + params.length}`;
    };
    const mapField = (field) => {
        if (!opts.allowedFields.includes(field)) {
            throw new Error(`applyFilter: field '${field}' is not in allowedFields whitelist (zod should have rejected earlier)`);
        }
        return opts.fieldMapping?.[field] ?? field;
    };
    const walkLeaf = (field, op, value) => {
        const col = mapField(field);
        if ((0, filter_types_1.isCrudArrayFilterOp)(op) && !(opts.arrayFields ?? []).includes(field)) {
            throw new Error(`applyFilter: op='${op}' only for arrayFields, '${field}' is not one (zod should have rejected earlier)`);
        }
        switch (op) {
            case 'eq':
                // value === null → IS NULL semantics (consistent с SQL NULL handling).
                return value === null ? `${col} IS NULL` : `${col} = ${addParam(value)}`;
            case 'neq':
                return value === null ? `${col} IS NOT NULL` : `${col} <> ${addParam(value)}`;
            case 'in':
                if (!Array.isArray(value)) {
                    throw new Error(`applyFilter: op='in' requires array value, got ${typeof value}`);
                }
                return `${col} = ANY(${addParam(value)})`;
            case 'nin':
                if (!Array.isArray(value)) {
                    throw new Error(`applyFilter: op='nin' requires array value, got ${typeof value}`);
                }
                return `${col} <> ALL(${addParam(value)})`;
            case 'like': {
                const pattern = likeMode === 'safe' ? (0, exports.escapeLikePattern)(String(value)) : String(value);
                return `${col} LIKE ${addParam(pattern)}`;
            }
            case 'ilike': {
                const pattern = likeMode === 'safe' ? (0, exports.escapeLikePattern)(String(value)) : String(value);
                return `${col} ILIKE ${addParam(pattern)}`;
            }
            case 'contains': {
                const pattern = `%${(0, exports.escapeLikePattern)(String(value))}%`;
                return `${col} ILIKE ${addParam(pattern)}`;
            }
            case 'starts_with': {
                const pattern = `${(0, exports.escapeLikePattern)(String(value))}%`;
                return `${col} ILIKE ${addParam(pattern)}`;
            }
            case 'ends_with': {
                const pattern = `%${(0, exports.escapeLikePattern)(String(value))}`;
                return `${col} ILIKE ${addParam(pattern)}`;
            }
            case 'gte':
                return `${col} >= ${addParam(value)}`;
            case 'lte':
                return `${col} <= ${addParam(value)}`;
            case 'gt':
                return `${col} > ${addParam(value)}`;
            case 'lt':
                return `${col} < ${addParam(value)}`;
            case 'is_null':
                return `${col} IS NULL`;
            case 'is_not_null':
                return `${col} IS NOT NULL`;
            // Параметр — JS-массив: node-pg шлёт его литералом `{…}` без типа, Postgres
            // приводит к типу колонки (text[], uuid[]) — без каста по месту. `@>`/`&&`
            // идут по GIN-индексу колонки.
            case 'any_eq':
                return `${col} @> ${addParam([value])}`;
            case 'any_in':
                if (!Array.isArray(value)) {
                    throw new Error(`applyFilter: op='any_in' requires array value, got ${typeof value}`);
                }
                return `${col} && ${addParam(value)}`;
            case 'any_contains': {
                const pattern = `%${(0, exports.escapeLikePattern)(String(value))}%`;
                return `EXISTS (SELECT 1 FROM unnest(${col}) AS elem WHERE elem::text ILIKE ${addParam(pattern)})`;
            }
        }
    };
    const walk = (node) => {
        if (node.and !== undefined) {
            if (node.and.length === 0)
                return 'TRUE';
            return '(' + node.and.map(walk).join(' AND ') + ')';
        }
        if (node.or !== undefined) {
            if (node.or.length === 0)
                return 'FALSE';
            return '(' + node.or.map(walk).join(' OR ') + ')';
        }
        if (node.not !== undefined) {
            return 'NOT (' + walk(node.not) + ')';
        }
        // Leaf — должен иметь field + op (zod гарантирует).
        if (node.field === undefined || node.op === undefined) {
            throw new Error('applyFilter: filter node has neither composition nor leaf (zod should have rejected)');
        }
        return walkLeaf(node.field, node.op, node.value);
    };
    const sql = walk(ast);
    return { sql, params };
};
exports.applyFilter = applyFilter;
/**
 * Escape LIKE/ILIKE pattern special chars (`%`, `_`, `\`) через `\` префикс.
 * Делает pattern буквальной строкой — wildcards не работают.
 *
 * Используется в `applyFilter` при `likeMode: 'safe'` (default).
 *
 * @example
 *   escapeLikePattern('100%')  // → '100\\%'
 *   escapeLikePattern('foo_bar') // → 'foo\\_bar'
 *   escapeLikePattern('a\\b')    // → 'a\\\\b'
 */
const escapeLikePattern = (raw) => {
    return raw.replace(/([%_\\])/g, '\\$1');
};
exports.escapeLikePattern = escapeLikePattern;
//# sourceMappingURL=filter-sql.js.map